import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { PartnerAccountViewModel } from "../../../api/generated/models/PartnerAccountViewModel";
import { PartnerAccountsService } from "../../../api/generated/services/PartnerAccountsService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface SendManuallyPartnerInvitationModalProps {
  isOpen: boolean;
  partnerAccount: PartnerAccountViewModel | null;
  onClose: () => void;
}

const SendManuallyPartnerInvitationModal: React.FC<SendManuallyPartnerInvitationModalProps> = ({
  isOpen,
  partnerAccount,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setInvitationLink(null);
      setCopied(false);
      onClose();
    }
  };

  const handleGetLink = async () => {
    if (!partnerAccount?.id) {
      setError("Partner account ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await PartnerAccountsService.postApiPartnerAccountsGetPartnerAccountInvitationLink(
        { partnerAccountId: partnerAccount.id }
      );
      setInvitationLink(response.invitationLink ?? null);
    } catch (err: any) {
      console.error("Error getting invitation link:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to get invitation link. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!invitationLink) return;
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement("textarea");
      el.value = invitationLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!partnerAccount) {
    return null;
  }

  return (
    <Modal
      className="modal-content md:max-w-[500px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Send Invitation Manually"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Send Invitation Manually</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
          <p className="text-sm text-orange-800">
            This user has not yet activated their account.
          </p>
          <p className="text-sm text-orange-800 mt-2">
            Generate an invitation link to send manually to{" "}
            <span className="font-medium">{partnerAccount.email}</span>.
          </p>
          <p className="text-sm text-orange-800 mt-2 font-medium">
            Note: Generating a new link will invalidate all previously sent invitation links.
          </p>
        </div>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        {invitationLink && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 font-medium">Invitation Link:</p>
            <div className="flex gap-2 items-start">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800 break-all font-mono">
                {invitationLink}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="shrink-0 mt-0.5"
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {invitationLink ? "Close" : "Cancel"}
          </Button>
          {!invitationLink && (
            <Button onClick={handleGetLink} isLoading={loading}>
              Generate Link
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SendManuallyPartnerInvitationModal;
